import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader, fmtDate, StatusBadge } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Upload, Paperclip, CheckCircle2, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";

export default function FollowupDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [action, setAction] = useState("");
  const [files, setFiles] = useState([]); // {id, filename}
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = () => api.get(`/followups/${id}`).then((r) => setData(r.data)).catch(() => setData(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (data === null) return <Loader label="Loading follow-up" />;
  if (data === false) return <div className="text-sm text-muted-foreground">Not available.</div>;

  const { followup: f, evidence } = data;
  const isAssignee = user.role === "tac" && f.assigned_to === user.id;
  const canVerify = ["validator", "am", "admin"].includes(user.role);
  const overdue = !["closed", "verified"].includes(f.status) && f.due_date && f.due_date < new Date().toISOString().slice(0, 10);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: res } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFiles((prev) => [...prev, { id: res.id, filename: res.filename }]);
      toast.success("Evidence uploaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const respond = async () => {
    try {
      await api.post(`/followups/${id}/respond`, { action_taken: action, evidence_file_ids: files.map((x) => x.id) });
      toast.success("Response submitted");
      setAction(""); setFiles([]);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const act = async (verb) => {
    try { await api.post(`/followups/${id}/${verb}`); toast.success(`Follow-up ${verb === "verify" ? "verified" : "closed"}`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader
        overline={`Governance follow-up · ${f.tac_name}`}
        title={f.title}
        description={f.description}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate("/followups")} className="rounded-md"><ArrowLeft className="h-4 w-4 mr-1.5" aria-hidden="true" /> Back</Button>
            {canVerify && f.status === "responded" && <Button variant="outline" onClick={() => act("verify")} className="rounded-md" data-testid="verify-followup-button"><ShieldCheck className="h-4 w-4 mr-1.5" aria-hidden="true" /> Verify</Button>}
            {canVerify && f.status !== "closed" && <Button onClick={() => act("close")} className="rounded-md" data-testid="close-followup-button"><CheckCircle2 className="h-4 w-4 mr-1.5" aria-hidden="true" /> Close</Button>}
          </div>
        }
      />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-card border border-border rounded-md p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="overline">Details</div>
              <StatusBadge status={f.status} />
            </div>
            <dl className="grid grid-cols-2 gap-3 text-sm">
              <div><dt className="text-muted-foreground text-xs uppercase tracking-wider">Due date</dt><dd className="font-mono">{f.due_date}{overdue && <span className="ml-2 inline-flex items-center rounded-full border border-rose-300 bg-rose-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-rose-700" data-testid="followup-overdue-badge">Overdue</span>}{f.escalation_level >= 3 && <span className="ml-1 inline-flex items-center rounded-full border border-purple-300 bg-purple-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-purple-700">Escalated to AM/PD</span>}</dd></div>
              <div><dt className="text-muted-foreground text-xs uppercase tracking-wider">Priority</dt><dd className="capitalize">{f.priority}</dd></div>
              <div><dt className="text-muted-foreground text-xs uppercase tracking-wider">Created by</dt><dd>{f.created_by_name}</dd></div>
              <div><dt className="text-muted-foreground text-xs uppercase tracking-wider">Created</dt><dd className="font-mono">{fmtDate(f.created_at)}</dd></div>
            </dl>
            {f.action_taken && (
              <div className="mt-4 pt-4 border-t border-border">
                <div className="overline mb-1">Partner response</div>
                <p className="text-sm">{f.action_taken}</p>
              </div>
            )}
            {evidence?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-border">
                <div className="overline mb-2">Evidence</div>
                <ul className="space-y-1.5">
                  {evidence.map((e) => (
                    <li key={e.id}>
                      <a href={`${API}/files/${e.id}/download`} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm text-foreground underline hover:text-slate-600" data-testid={`evidence-${e.id}`}>
                        <Paperclip className="h-3.5 w-3.5" aria-hidden="true" /> {e.filename}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {isAssignee && f.status !== "closed" && f.status !== "verified" && (
            <div className="bg-card border border-border rounded-md p-5" data-testid="respond-panel">
              <div className="overline mb-3">Respond with action taken</div>
              <Textarea value={action} onChange={(e) => setAction(e.target.value)} placeholder="Describe the action taken to address this follow-up…" className="rounded-md" data-testid="action-taken-input" />
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <input ref={fileRef} type="file" onChange={upload} className="hidden" data-testid="evidence-file-input" />
                <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-md" data-testid="upload-evidence-button">
                  <Upload className="h-4 w-4 mr-1.5" aria-hidden="true" /> {uploading ? "Uploading…" : "Attach evidence"}
                </Button>
                {files.map((x) => (
                  <span key={x.id} className="inline-flex items-center gap-1 text-xs bg-muted rounded-md px-2 py-1 font-mono">
                    <Paperclip className="h-3 w-3" aria-hidden="true" />{x.filename}
                    <button onClick={() => setFiles(files.filter((y) => y.id !== x.id))} aria-label="remove"><X className="h-3 w-3" /></button>
                  </span>
                ))}
                <Button onClick={respond} disabled={!action} className="rounded-md ml-auto" data-testid="submit-response-button">Submit response</Button>
              </div>
            </div>
          )}
        </div>

        <div className="bg-card border border-border rounded-md p-5">
          <div className="overline mb-4">Activity timeline</div>
          <ol className="space-y-4">
            {(f.timeline || []).map((t, i) => (
              <li key={i} className="relative pl-5" data-testid={`timeline-${i}`}>
                <span className="absolute left-0 top-1.5 h-2 w-2 rounded-full bg-slate-900" aria-hidden="true" />
                {i < f.timeline.length - 1 && <span className="absolute left-[3px] top-3 bottom-[-16px] w-px bg-border" aria-hidden="true" />}
                <div className="text-sm font-medium capitalize">{t.action}</div>
                {t.note && <div className="text-xs text-muted-foreground mt-0.5">{t.note}</div>}
                <div className="text-[11px] text-muted-foreground font-mono mt-0.5">{t.by} · {fmtDate(t.at)}</div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}
